import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Loader2, RotateCw } from 'lucide-react';
import '../styles/skpi/tokens.css';
import '../styles/skpi/kit.css';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { BU_OPTIONS, todayYmd, useStrategicKpis } from '../hooks/useStrategicKpis';
import { DashboardTab } from '../components/strategic-kpis/DashboardTab';
import { PeopleTab, peopleFamily } from '../components/strategic-kpis/PeopleTab';
import { PersonPanel } from '../components/strategic-kpis/PersonPanel';
import { DrillDownTable, type Hierarchy } from '../components/strategic-kpis/DrillDownTable';
import { EvidencePanel } from '../components/strategic-kpis/EvidencePanel';
import { EvidenceDrill } from '../components/strategic-kpis/EvidenceDrill';
import { DefinitionsTab } from '../components/strategic-kpis/DefinitionsTab';
import { DataQualityTab } from '../components/strategic-kpis/DataQualityTab';
import { VersionHistoryPanel } from '../components/strategic-kpis/VersionHistoryPanel';
import { TargetsTab } from '../components/strategic-kpis/TargetsTab';
import { RolloverPanel } from '../components/strategic-kpis/targets/RolloverPanel';
import { AuditPanel } from '../components/strategic-kpis/targets/AuditPanel';
import { useToast } from '../components/ui/Toast';
import { strategicKpisApi } from '../services/strategicKpisApi';
import { PageHead, SKPI_TABPANEL_ID, skpiTabId, type PageHeadTab } from '../components/strategic-kpis/shell/PageHead';
import { FilterRow, type FilterSpec } from '../components/strategic-kpis/shell/FilterRow';
import { ActiveRow, selectionInfo, type ActiveChip } from '../components/strategic-kpis/shell/ActiveRow';
import type { ActualsResponse, EvidenceNode, EvidenceScopeType, KpiDefinition, KpiSelection, KpiTargetAuditRow, RolloverResult } from '../types/strategicKpis';
import { contextKpis, steeredKpis } from '../utils/strategicKpiSelectors';
import { countDataQualityChecks } from '../utils/strategicKpiDataQuality';
import type { SelectionList, SelectionOptions } from '../utils/strategicKpiSelection';
import {
    clearRetiredSkpiStorage, normalizeSkpiParams, parseSkpiUrl, peopleStateOf, readStoredPeopleView, storePeopleView,
    withDrill, withSkpiState, withTab,
    type PeopleState, type PeopleView, type SkpiTab, type SkpiUrlOptions, type SkpiUrlState,
} from '../utils/strategicKpiUrl';
import { useWhatsNewAnchor, NewMarker, SPOTLIGHT_CLASS } from '../whats-new';
import { formatDate, formatDateTimeCH } from '../utils/format';

const TAB_LABEL: Record<SkpiTab, string> = {
    dashboard: 'Dashboard',
    people: 'People',
    targets: 'Targets',
    quality: 'Data quality',
    definitions: 'Definitions',
};
const TAB_ORDER: readonly SkpiTab[] = ['dashboard', 'people', 'targets', 'quality', 'definitions'];

const FIRST_YEAR = 2024;   // the first year with migrated targets (migration 187)
/** The change log shows the latest rows only (as the audit table did before the redesign). */
const AUDIT_LIMIT = 500;
const HIDE_STORAGE_KEY = 'strategic-kpis-hide-no-targets';
/** What's New entry 2026-10-07-skpi-selection-filter marks the filter (K3); it sits on the filter row (§2 rule 12). */
const FILTER_ANCHOR = 'filter:skpi-selection';
const SELECTION_LISTS: readonly SelectionList[] = ['companies', 'teams', 'persons'];
const LIST_LABEL: Record<SelectionList, string> = { companies: 'Company', teams: 'Team', persons: 'Person' };
const LIST_PLACEHOLDER: Record<SelectionList, string> = { companies: 'All companies', teams: 'All teams', persons: 'All persons' };
/** BU swatches the kit knows (`.bu.spc`, `.bu.pla`); the other options show none. */
const BU_SWATCH: Record<string, string> = { SPC: 'spc', PLA: 'pla' };
const DATA_STATE_TITLE = 'The replica is copied nightly Sunday to Thursday; these are the latest timestamps in it';

/**
 * The one panel state of the page (masterplan skpi-redesign §2 rule 6, §4 «Page-Root»). Phase B has two
 * kinds on the dashboard and People: the evidence of a number (E9; every number that opens evidence sets it) and
 * a person (E16). The Targets tab has two more (C5): the rollover assistant (E10) and the change log (E5). The
 * Definitions tab has one (D4): the version history (E14). A kind sets `.panel-open` on the root and renders inside `SidePanel`
 * next to `.skpi-main`; one panel at a time, a new kind replaces the old one.
 */
type PanelState =
    | { kind: 'evidence'; node: EvidenceNode }
    | { kind: 'person'; staffId: string }
    | { kind: 'rollover'; sourceYear?: number; targetYear?: number }
    | { kind: 'audit' }
    | { kind: 'versions' };

/** The tabs whose content has a side panel (the others never show one, and leaving a tab closes it). */
const PANEL_TABS: readonly SkpiTab[] = ['dashboard', 'people', 'targets', 'definitions'];
/** Which panel kinds belong to which tab: a kind never shows beside another tab's content. */
const PANEL_KINDS: Partial<Record<SkpiTab, readonly PanelState['kind'][]>> = {
    dashboard: ['evidence', 'person'],
    people: ['evidence', 'person'],
    targets: ['rollover', 'audit'],
    definitions: ['versions'],
};

/**
 * Strategic KPIs (masterplan skpi-redesign, phase B: dashboard, evidence and People on the sales-app frame).
 * The root is `.skpi.skpi-root` (+ `.panel-open` while a side panel is open); the head is the title with the
 * five tabs as a segment, the filter row (BU, year, as-of date | company, team, person) and the chip row
 * (one chip per set list, Reset all, the data state, the info icon with the former selection notice). Tabs:
 *   Dashboard     DashboardTab: family pills, the context KPIs, the one table of steered KPIs (E4)
 *   People        PeopleTab: ranking per KPI (Leaderboard) or the matrix of ONE family (DrillDownTable, E18);
 *                 a ranking row opens the person panel (E16)
 *   Targets       TargetsTab: status line and the one TargetGrid (C3b); the rollover assistant and the change log are
 *                 side panels this page renders (C5). URL: level and fam. Audit rows load only while that panel is open
 *   Data quality  DataQualityTab (D1): the checks with findings (meetings without a company mapping, persons without a
 *                 target); the tab counts them; «Set targets» leads to the Targets tab
 *   Definitions   the catalogue as a reading document; the version history is a side panel (D4)
 * Everything shown is the engine's output (GET actuals / evidence / definitions); the page formats, counts
 * lights and sorts, it does not calculate. A stored target reloads the dashboard past its cache.
 *
 * Evidence (E9): every number that opens evidence (steered row, context KPI, ranking row, matrix cell, a figure
 * in the person panel) sets the page's one panel state and EvidencePanel draws that node from the payload.
 * «Show the rows» pushes the drill address (`withDrill`, the one history entry) and EvidenceDrill replaces the
 * tab content; Back writes the address without the drill params, a reloaded drill link opens the same view.
 *
 * The address (utils/strategicKpiUrl): tab, the People params and the drill live in the URL; all but the drill
 * with `replace`. An old link (`?view=leaderboard|matrix`, or only kpi/group/cell/hier) lands on People and is
 * rewritten once into its canonical form. BU, year and the selection are useStrategicKpis' own params. Retired
 * browser keys are deleted on load (B: the matrix column pills and the evidence modal's column widths; C: the Targets
 * column pills and the column widths of the old person grid, group grid and audit table).
 *
 * E22: the People state (view, KPI, grouping, family, cell, hierarchy) survives a tab change. The page remembers
 * it when it leaves People and `withTab` restores it on the way back; the last chosen view also stays in
 * localStorage `strategic-kpis-drill-view` and fills a People address that names none.
 */
export function StrategicKpisPage() {
    const { canView, canEdit, isLoading: permLoading } = usePagePermissions('/strategic-kpis');
    const kpi = useStrategicKpis(canView);
    const [params, setParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    /** Marks the history entry that «Show the rows» pushed; Back then goes back instead of adding an entry. */
    const drillPushed = (location.state as { skpiDrillPushed?: boolean } | null)?.skpiDrillPushed === true;
    // E22: the last chosen People view (a per-person preference, as before the redesign).
    const [storedView, setStoredView] = useState<PeopleView | null>(() => readStoredPeopleView());
    const urlOpts: SkpiUrlOptions = { storedView };
    const url = parseSkpiUrl(params, urlOpts);
    const { tab, drill } = url;
    /** The People state as the page left it, restored when People is entered again (E22). */
    const peopleMemory = useRef<PeopleState | null>(null);
    const [panel, setPanel] = useState<PanelState | null>(null);
    const { toast } = useToast();
    // C5: Targets reloads its targets and prior year after a real rollover; the change log has its own loader.
    const [targetsRefresh, setTargetsRefresh] = useState(0);
    const [audit, setAudit] = useState<KpiTargetAuditRow[]>([]);
    const [auditAllYears, setAuditAllYears] = useState(false);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditReload, setAuditReload] = useState(0);
    const [defs, setDefs] = useState<KpiDefinition[] | null>(null);

    /** Changes the page's own params (never bu, year or the selection), always with replace. */
    const patchUrl = (patch: Partial<SkpiUrlState>) => setParams(prev => withSkpiState(prev, patch, urlOpts), { replace: true });
    const setTab = (t: SkpiTab) => {
        setPanel(null);   // a panel belongs to the tab it was opened on
        setParams(prev => {
            const cur = parseSkpiUrl(prev, urlOpts);
            if (cur.tab === 'people') peopleMemory.current = peopleStateOf(cur);
            return withTab(prev, t, { ...urlOpts, people: peopleMemory.current });
        }, { replace: true });
    };
    // E9: «Show the rows» is the one change that pushes a history entry, so the browser's Back returns to the table.
    const showRows = (node: EvidenceNode) => {
        setPanel(null);
        setParams(prev => withDrill(prev, { kpiKey: node.kpiKey, scopeType: node.scopeType, scopeKey: node.scopeKey }, urlOpts), { state: { skpiDrillPushed: true } });
    };
    // Back: when this session pushed the drill entry, history.back() returns to the table without a second entry;
    // a reloaded or shared drill link has no such entry, so Back writes the address without the drill params (replace).
    const leaveDrill = () => {
        if (drillPushed) navigate(-1);
        else setParams(prev => withDrill(prev, null, urlOpts), { replace: true });
    };
    // The marker stays on the entry while the drill's KPI changes (replace keeps the state of its own entry).
    const changeDrillKpi = (kpiKey: string) => setParams(prev => {
        const cur = parseSkpiUrl(prev, urlOpts).drill;
        return cur ? withDrill(prev, { ...cur, kpiKey }, urlOpts) : prev;
    }, { replace: true, state: location.state });
    const setView = (v: PeopleView) => {
        storePeopleView(v);
        setStoredView(v);
        setParams(prev => withSkpiState(prev, { view: v }, { storedView: v }), { replace: true });
    };

    // An old or invalid address is rewritten once into its canonical form (rule 11); a canonical one is left alone.
    const paramsKey = params.toString();
    useEffect(() => {
        const next = normalizeSkpiParams(params, urlOpts);
        if (next) setParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paramsKey, storedView]);
    // Browser keys whose owner has left: A none (E22 keeps the drill view), B the matrix column pills and the
    // evidence modal's column widths, C the Targets column pills and the column widths of the old person grid,
    // group grid and audit table. `strategic-kpis-drill-view` stays.
    useEffect(() => { clearRetiredSkpiStorage('C'); }, []);

    const [hideNoTargets, setHideNoTargets] = useState<boolean>(() => loadStored(HIDE_STORAGE_KEY, '1', v => v === '0' || v === '1') === '1');
    // No `reveal`: the filter row sits in the page head, always in view on the tabs it applies to.
    const filterAnchor = useWhatsNewAnchor(FILTER_ANCHOR);
    const changeSelection = (s: KpiSelection) => { filterAnchor.markUsed(); kpi.setSelection(s); };

    const data = kpi.data;
    const steered = useMemo(() => data ? steeredKpis(data) : [], [data]);
    const context = useMemo(() => data ? contextKpis(data) : [], [data]);

    // A person panel whose person is not in the new payload (a filter change removed them) closes; it must not come back later.
    useEffect(() => {
        if (data && panel?.kind === 'person' && !data.persons.some(p => p.staffId === panel.staffId)) setPanel(null);
    }, [data, panel]);

    // The change log loads only while its panel is open (not on every tab change); a stored change, a real rollover,
    // the year, the all-years switch and the panel's reload button load it again.
    const auditOpen = tab === 'targets' && panel?.kind === 'audit';
    useEffect(() => {
        if (!auditOpen || !canView) return;
        let cancelled = false;
        setAuditLoading(true);
        setAudit([]);   // no rows of the old year (or of a failed load) under the new heading
        strategicKpisApi.audit(auditAllYears ? { limit: AUDIT_LIMIT } : { year: kpi.year, limit: AUDIT_LIMIT })
            .then(r => { if (!cancelled) setAudit(r); })
            .catch(e => { if (!cancelled) toast(e instanceof Error ? e.message : 'Failed to load the change log', 'error'); })
            .finally(() => { if (!cancelled) setAuditLoading(false); });
        return () => { cancelled = true; };
    }, [auditOpen, canView, kpi.year, auditAllYears, auditReload, toast]);
    // Units of the old and new values: the catalogue is read once, when the change log is first opened.
    useEffect(() => {
        if (!auditOpen || defs !== null) return;
        strategicKpisApi.definitions().then(setDefs).catch(() => { /* values show as counts until it loads */ });
    }, [auditOpen, defs]);
    const unitOf = (kpiKey: string) => defs?.find(d => d.KpiKey === kpiKey)?.Unit ?? data?.kpis.find(k => k.kpiKey === kpiKey)?.unit ?? 'count';
    // After a real rollover: the Targets tab loads its targets again, the dashboard recomputes past its cache, the log reloads.
    const rolloverDone = (_result: RolloverResult) => {
        setTargetsRefresh(n => n + 1);
        setAuditReload(n => n + 1);
        kpi.reload();
    };

    const toggleHide = () => setHideNoTargets(h => { store(HIDE_STORAGE_KEY, h ? '0' : '1'); return !h; });

    const openEvidence = (kpiKey: string, scopeType: EvidenceScopeType, scopeKey: string, label: string, partial?: boolean) =>
        setPanel({ kind: 'evidence', node: { kpiKey, scopeType, scopeKey, label, partial } });
    const openPerson = (staffId: string) => setPanel({ kind: 'person', staffId });
    // The Targets tab at its default level (persons); the page has no URL param for a single person or BU.
    const editTargets = () => setTab('targets');

    if (permLoading) {
        return <div className="flex h-64 items-center justify-center" role="status"><Loader2 className="h-6 w-6 animate-spin text-blue-500" aria-hidden="true" /><span className="sr-only">Loading</span></div>;
    }
    if (!canView) {
        return <div className="flex h-64 items-center justify-center"><p className="text-gray-500">You do not have access to this page.</p></div>;
    }

    const today = todayYmd();
    const thisYear = Number(today.slice(0, 4));
    const years: number[] = [];
    for (let y = thisYear + 1; y >= FIRST_YEAR; y--) years.push(y);
    const dqChecks = countDataQualityChecks(data);
    const withSelection = tab !== 'definitions';

    const tabs: PageHeadTab<SkpiTab>[] = TAB_ORDER.map(key => key === 'quality'
        ? { key, label: TAB_LABEL[key], count: dqChecks, countLabel: dqChecks === 1 ? 'check to look at' : 'checks to look at' }
        : { key, label: TAB_LABEL[key] });

    const options = kpi.options;
    const fields: FilterSpec[] = [
        {
            kind: 'select', key: 'bu', label: 'Business unit', value: kpi.bu, testId: 'bu-select',
            options: BU_OPTIONS.map(o => ({ value: o.value, label: o.label })),
            onChange: kpi.setBu,
            set: kpi.bu !== BU_OPTIONS[0].value,
            lead: BU_SWATCH[kpi.bu] ? <span className={`bu ${BU_SWATCH[kpi.bu]}`} aria-hidden="true" /> : undefined,
        },
        {
            kind: 'select', key: 'year', label: 'Year', value: String(kpi.year), testId: 'year-select',
            options: years.map(y => ({ value: String(y), label: String(y) })),
            onChange: v => kpi.setYear(Number(v)),
            set: kpi.year !== thisYear,
        },
        { kind: 'date', key: 'asof', label: 'As-of date', value: kpi.asOf, onChange: kpi.setAsOf, set: kpi.asOf !== today, testId: 'asof-input' },
        ...(withSelection ? selectionFields(kpi.selection, options, changeSelection, filterAnchor.isNew) : []),
    ];

    const chips: ActiveChip[] = withSelection ? selectionChips(kpi.selection, options, changeSelection) : [];
    const buLabel = BU_OPTIONS.find(o => o.value === kpi.bu)?.label ?? kpi.bu;
    const emptyText = !withSelection ? 'Definitions apply to every selection'
        : kpi.bu === 'ALL' ? `No selection: ${buLabel}` : `No selection: the whole ${buLabel} business unit`;
    const focusFirstList = () => document.getElementById('skpi-filter-companies')?.focus();

    // E9: the drill view replaces the tab content; a side panel exists only beside the dashboard and People (no drill).
    const showDrill = drill !== null;
    const kindHere = !showDrill && panel !== null && PANEL_TABS.includes(tab) && (PANEL_KINDS[tab] ?? []).includes(panel.kind);
    // Evidence and person draw from the payload; rollover and audit do not need it.
    const panelHere = kindHere && panel && (data || panel.kind === 'rollover' || panel.kind === 'audit' || panel.kind === 'versions') ? panel : null;
    const panelPerson = panelHere?.kind === 'person' ? data?.persons.find(p => p.staffId === panelHere.staffId) ?? null : null;
    const panelOpen = panelHere?.kind === 'evidence' || panelHere?.kind === 'rollover' || panelHere?.kind === 'audit' || panelHere?.kind === 'versions' || panelPerson !== null;

    return (
        <div className={`skpi skpi-root${panelOpen ? ' panel-open' : ''}`}>
            <div className="skpi-main">
                <PageHead
                    title="Strategic KPIs"
                    sub="Actuals vs targets and prior year per BU, company, team and person"
                    tabs={tabs}
                    active={tab}
                    onSelect={setTab}
                    ariaLabel="Strategic KPIs"
                />

                <FilterRow
                    fields={fields}
                    anchorClassName={filterAnchor.spotlight ? SPOTLIGHT_CLASS : undefined}
                    testId="selection-filter"
                    trailing={
                        <div className="flex items-center gap-2 self-center">
                            <button
                                type="button"
                                onClick={kpi.reload}
                                disabled={kpi.loading}
                                className="btn secondary sm"
                                title="Recompute now, bypassing the 5-minute cache"
                            >
                                <RotateCw className={`i sm${kpi.loading ? ' animate-spin motion-reduce:animate-none' : ''}`} aria-hidden="true" /> Refresh
                            </button>
                            {/* S3-10: a live region that exists before it speaks, so "Computing…" is announced. */}
                            <span className="muted" role="status">{kpi.loading ? 'Computing…' : ''}</span>
                        </div>
                    }
                />

                <ActiveRow
                    chips={chips}
                    emptyText={emptyText}
                    onReset={withSelection ? () => { changeSelection({ companies: [], teams: [], persons: [] }); window.requestAnimationFrame(focusFirstList); } : undefined}
                    onEmptied={focusFirstList}
                    dataState={data ? {
                        text: `HubSpot sync ${formatDateTimeCH(data.dataAsOf.hubspotSyncTime)} · last posting ${formatDate(data.dataAsOf.maxPostingDate)} · last meeting ${formatDateTimeCH(data.dataAsOf.maxMeetingStart)}`,
                        title: DATA_STATE_TITLE,
                    } : null}
                    info={withSelection && data ? selectionInfo(data) : null}
                />

                {kpi.error && (
                    <div role="alert" className="banner error">
                        <AlertTriangle className="i ic" aria-hidden="true" />
                        <p>{friendlyError(kpi.error)}</p>
                    </div>
                )}

                <div role="tabpanel" id={SKPI_TABPANEL_ID} aria-labelledby={skpiTabId(tab)} className="space-y-4">
                    {showDrill && drill && !data && !kpi.error && (
                        <div className="muted p-12 text-center"><Loader2 className="mr-2 inline h-5 w-5 animate-spin" aria-hidden="true" />Computing the KPIs…</div>
                    )}

                    {/* No request before the payload is in: the drill's selection comes from it (a reloaded link has none yet). */}
                    {showDrill && drill && data && (
                        <EvidenceDrill
                            node={drill}
                            data={data}
                            year={data.year}
                            asOf={data.asOf}
                            bus={data.bus}
                            selection={data.selection ? { companies: data.selection.companies, teams: data.selection.teams, persons: data.selection.persons } : null}
                            originLabel={TAB_LABEL[tab]}
                            onBack={leaveDrill}
                            onKpiChange={changeDrillKpi}
                        />
                    )}

                    {!showDrill && tab === 'definitions' && <DefinitionsTab actuals={data} onOpenVersions={() => setPanel({ kind: 'versions' })} />}

                    {!showDrill && tab === 'targets' && (
                        <TargetsTab
                            year={kpi.year}
                            bus={kpi.bus}
                            selection={kpi.selection}
                            actuals={data && data.year === kpi.year ? data : null}
                            canEdit={canEdit}
                            onChanged={kpi.reload}
                            firstYear={FIRST_YEAR}
                            level={url.level}
                            onLevelChange={level => patchUrl({ level })}
                            fam={url.fam}
                            onFamChange={fam => patchUrl({ fam })}
                            onOpenRollover={(sourceYear?: number, targetYear?: number) => setPanel({ kind: 'rollover', sourceYear, targetYear })}
                            onOpenAudit={() => setPanel({ kind: 'audit' })}
                            onSaved={() => setAuditReload(n => n + 1)}
                            refreshToken={targetsRefresh}
                        />
                    )}

                    {!showDrill && (tab === 'dashboard' || tab === 'people' || tab === 'quality') && !data && !kpi.error && (
                        <div className="muted p-12 text-center"><Loader2 className="mr-2 inline h-5 w-5 animate-spin" aria-hidden="true" />Computing the KPIs…</div>
                    )}

                    {!showDrill && tab === 'dashboard' && data && (
                        <DashboardTab
                            data={data}
                            steered={steered}
                            context={context}
                            onOpenEvidence={openEvidence}
                            selectedKpiKey={panelHere?.kind === 'evidence' ? panelHere.node.kpiKey : null}
                            loading={kpi.loading}
                        />
                    )}

                    {!showDrill && tab === 'people' && data && (
                        <PeopleToday
                            data={data}
                            loading={kpi.loading}
                            url={url}
                            onPatch={patchUrl}
                            onView={setView}
                            steered={steered}
                            hideNoTargets={hideNoTargets}
                            onToggleHide={toggleHide}
                            onOpenEvidence={openEvidence}
                            onOpenPerson={openPerson}
                            selectedStaffId={panelPerson?.staffId ?? null}
                        />
                    )}

                    {!showDrill && tab === 'quality' && data && <DataQualityTab data={data} onSetTargets={() => editTargets()} />}
                </div>
            </div>

            {panelHere?.kind === 'rollover' && (
                <RolloverPanel
                    defaultSourceYear={panelHere.sourceYear ?? kpi.year - 1}
                    defaultTargetYear={panelHere.targetYear ?? kpi.year}
                    canEdit={canEdit}
                    firstYear={FIRST_YEAR}
                    maxYear={thisYear + 1}
                    onDone={rolloverDone}
                    onClose={() => setPanel(null)}
                />
            )}
            {panelHere?.kind === 'audit' && (
                <AuditPanel
                    rows={audit}
                    loading={auditLoading}
                    year={kpi.year}
                    allYears={auditAllYears}
                    onToggleAllYears={setAuditAllYears}
                    onRefresh={() => setAuditReload(n => n + 1)}
                    unitOf={unitOf}
                    onClose={() => setPanel(null)}
                />
            )}
            {panelHere?.kind === 'versions' && <VersionHistoryPanel onClose={() => setPanel(null)} />}
            {data && panelHere?.kind === 'evidence' && (
                <EvidencePanel
                    data={data}
                    node={panelHere.node}
                    onClose={() => setPanel(null)}
                    onShowRows={showRows}
                    onOpenNode={node => setPanel({ kind: 'evidence', node })}
                />
            )}
            {data && panelPerson && (
                <PersonPanel
                    data={data}
                    person={panelPerson}
                    steered={steered}
                    onOpenEvidence={openEvidence}
                    onEditTargets={editTargets}
                    onClose={() => setPanel(null)}
                />
            )}
        </div>
    );
}

/**
 * K3: the three lists as filter fields. Options come from the persons of the year (cascading — a list offers
 * what fits the other two); until they are in, a chosen value shows its id. OR within a list, AND between
 * lists, always inside the BU.
 */
function selectionFields(selection: KpiSelection, options: SelectionOptions | null, onChange: (s: KpiSelection) => void, isNew: boolean): FilterSpec[] {
    return [
        { kind: 'sep', key: 'sep-selection' },
        ...SELECTION_LISTS.map((k, i): FilterSpec => ({
            kind: 'multi',
            key: k,
            label: LIST_LABEL[k],
            options: options?.[k] ?? selection[k].map(id => ({ id, name: id })),
            values: selection[k],
            onChange: values => onChange({ ...selection, [k]: values }),
            // Before the options are in, «All (0)» would be wrong.
            allText: options ? undefined : LIST_PLACEHOLDER[k],
            marker: i === 0 && isNew ? <NewMarker label="New: filter by company, team and person" /> : undefined,
        })),
    ];
}

/** One chip per set list: «Company  DIT, DPT» or «Person  A. Muster +2»; × clears that list. */
function selectionChips(selection: KpiSelection, options: SelectionOptions | null, onChange: (s: KpiSelection) => void): ActiveChip[] {
    return SELECTION_LISTS.filter(k => selection[k].length > 0).map(k => {
        const names = selection[k].map(id => options?.[k].find(o => o.id === id)?.name ?? id);
        return {
            key: k,
            kind: LIST_LABEL[k],
            value: names.length <= 2 ? names.join(', ') : `${names[0]} +${names.length - 1}`,
            title: names.join('\n'),
            onRemove: () => onChange({ ...selection, [k]: [] }),
        };
    });
}

/**
 * People (B3, B3b, E16, E18): PeopleTab holds the toolbar and the one table; the page feeds it the URL state and
 * the matrix. The ranking row opens the person panel; the family and cell segments are PeopleTab's, so the matrix
 * gets `fam` and `cellMode` as fixed values and draws no segment of its own. The matrix keeps its hierarchy
 * select and «Hide persons without targets» as `matrixControls`.
 *
 * The matrix gets every KPI of the payload (data.kpis) and narrows them to one family itself (E18). The person
 * search belongs to the ranking only; PeopleTab calls `matrix` with an empty text and the payload goes in untouched.
 */
function PeopleToday({ data, loading, url, onPatch, onView, steered, hideNoTargets, onToggleHide, onOpenEvidence, onOpenPerson, selectedStaffId }: {
    data: ActualsResponse;
    loading: boolean;
    url: SkpiUrlState;
    onPatch: (patch: Partial<SkpiUrlState>) => void;
    /** Changes the view and remembers it (localStorage, E22). */
    onView: (v: PeopleView) => void;
    steered: ReturnType<typeof steeredKpis>;
    hideNoTargets: boolean;
    onToggleHide: () => void;
    onOpenEvidence: (kpiKey: string, scopeType: EvidenceScopeType, scopeKey: string, label: string, partial?: boolean) => void;
    onOpenPerson: (staffId: string) => void;
    selectedStaffId: string | null;
}) {
    // E18: the matrix offers every family the payload has a KPI in (steered or not), the same list the tabs use.
    const fam = peopleFamily(data.kpis, url.fam);
    const matrixControls = (
        <>
            <label className="inline-flex items-center gap-1 text-gray-600">
                Hierarchy
                <select value={url.hier} onChange={e => onPatch({ hier: e.target.value as Hierarchy })} className="h-8 rounded border px-1.5 text-xs" data-testid="hierarchy-select">
                    <option value="company-team">BU › Company › Team › Person</option>
                    <option value="team">BU › Team › Person</option>
                </select>
            </label>
            <label className="inline-flex h-8 cursor-pointer items-center gap-1.5 text-gray-600" title="Persons without any stored target for this year are shown as one line per node instead of rows of zeros">
                <input type="checkbox" checked={hideNoTargets} onChange={onToggleHide} className="h-3.5 w-3.5 rounded border-gray-300" data-testid="hide-no-targets" />
                Hide persons without targets
            </label>
        </>
    );
    return (
        <PeopleTab
            data={data}
            loading={loading}
            steered={steered}
            view={url.view}
            onView={onView}
            kpi={url.kpi}
            onKpi={k => onPatch({ kpi: k })}
            group={url.group}
            onGroup={g => onPatch({ group: g })}
            fam={fam}
            onFam={f => onPatch({ fam: f })}
            cell={url.cell}
            onCell={c => onPatch({ cell: c })}
            matrixControls={matrixControls}
            matrix={() => (
                <DrillDownTable
                    data={data}
                    kpis={data.kpis}
                    hierarchy={url.hier}
                    hidePersonsWithoutTargets={hideNoTargets}
                    onOpenEvidence={onOpenEvidence}
                    fam={fam}
                    cellMode={url.cell}
                />
            )}
            onOpenEvidence={onOpenEvidence}
            onOpenPerson={onOpenPerson}
            selectedStaffId={selectedStaffId}
        />
    );
}

function store(key: string, value: string) {
    try { window.localStorage.setItem(key, value); } catch { /* ignore */ }
}

function loadStored<T extends string>(key: string, fallback: T, valid: (v: string) => boolean): T {
    try {
        const raw = window.localStorage.getItem(key);
        if (raw !== null && valid(raw)) return raw as T;
    } catch { /* fall through */ }
    return fallback;
}

/** S3-07: the engine's 400 for lists that exclude each other, with the way out. */
function friendlyError(message: string): string {
    if (/selection matches no person/i.test(message)) return 'No person matches this selection — remove a value or use Reset all.';
    return message;
}

export default StrategicKpisPage;
